import { Field } from "@/components/molecules/field";
import { FileUpload } from "@/components/molecules/file-upload";
import { differs, FormFooter } from "@/components/molecules/form-footer";
import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ChatHeader } from "@/chat/components/chat/ChatHeader";
import { ChatInput } from "@/chat/components/chat/ChatInput";
import { ChatLayout } from "@/chat/components/chat/ChatLayout";
import { ChatMessages } from "@/chat/components/chat/ChatMessages";
import type { PublicSessionInfo } from "@/chat/hooks/use-public-session-info";
import type { SessionMessage } from "@/chat/types";
import {
  useMyClients,
  useUpdateClient,
} from "@/features/clients/hooks/use-clients";
import { useConnectors } from "@/features/connectors/hooks/use-connectors";
import { useAgents } from "@/features/agents/hooks/use-agents";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import type { ClientBranding } from "@/features/clients/types";
import { cn } from "@/lib/utils";
import { ChatCircleDotsIcon, Copy, PhoneIcon } from "@phosphor-icons/react";
import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";
import { useTranslation } from "@/features/utils/i18n";
import { Trans } from "react-i18next";

const DEFAULT_COLOR = "#1BA4FF";

// Named corner-radius presets — a select is friendlier than a raw px slider.
// Each is labelled by `connectors.branding.radius.<label>`.
const RADIUS_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "square" },
  { value: 8, label: "slight" },
  { value: 12, label: "medium" },
  { value: 16, label: "rounded" },
  { value: 24, label: "veryRounded" },
];

export function ConnectorsPersonalizationPage() {
  const { t } = useTranslation("connectors");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: clients = [] } = useMyClients();
  const { data: connectors = [] } = useConnectors(clientId || "");
  const { data: agents = [] } = useAgents();
  const updateClient = useUpdateClient();
  const isOwner = useHasRole("OWNER");

  const currentClient = useMemo(
    () => clients.find((c) => c.client.id === clientId),
    [clients, clientId],
  );
  const saved = currentClient?.client.branding || {};

  const webConnector = connectors.find((c) => c.type === "web");
  // The chat greets with the agent configured on the web connector (else the
  // default first agent) — same resolution as the backend session start.
  const connectorAgent =
    (webConnector?.agent_id
      ? agents.find((a) => a.id === webConnector.agent_id)
      : undefined) || agents[0];
  const agentName = connectorAgent?.name || t("branding.defaultAgentName");

  const [logo, setLogo] = useState<File | string | null>(saved.logo || null);
  const [themeColor, setThemeColor] = useState(
    saved.theme_color || DEFAULT_COLOR,
  );
  const [radius, setRadius] = useState<number>(saved.radius ?? 16);
  const [shadow, setShadow] = useState<NonNullable<ClientBranding["shadow"]>>(
    saved.shadow || "none",
  );
  const [launcherLabel, setLauncherLabel] = useState(
    saved.launcher_label ?? "",
  );
  const [launcherPosition, setLauncherPosition] = useState<"right" | "left">(
    saved.launcher_position || "right",
  );
  const [launcherIcon, setLauncherIcon] = useState<"chat" | "phone">(
    saved.launcher_icon || "chat",
  );
  // Unset means the default, and the default is now "folded" — a labelled pill takes
  // half the width of a phone and lands on the merchant's own content.
  const [launcherCollapseMobile, setLauncherCollapseMobile] = useState<boolean>(
    saved.launcher_collapse_mobile ?? true,
  );
  const [hidePoweredBy, setHidePoweredBy] = useState<boolean>(
    saved.hide_powered_by ?? false,
  );
  // Unset is `auto`: the visitor's OS decides, which is what every chat did before.
  const [theme, setTheme] = useState<NonNullable<ClientBranding["theme"]>>(
    saved.theme || "auto",
  );

  // What is saved, with the defaults the fields show for what is not — the
  // reference both the re-hydration and the save bar's "modified" compare to.
  const savedValues = {
    logo: saved.logo || null,
    theme_color: saved.theme_color || DEFAULT_COLOR,
    radius: saved.radius ?? 16,
    shadow: saved.shadow || "none",
    launcher_label: saved.launcher_label ?? "",
    launcher_position: saved.launcher_position || "right",
    launcher_icon: saved.launcher_icon || "chat",
    launcher_collapse_mobile: saved.launcher_collapse_mobile ?? true,
    hide_powered_by: saved.hide_powered_by ?? false,
    theme: saved.theme || "auto",
  };

  const reset = () => {
    setLogo(savedValues.logo);
    setThemeColor(savedValues.theme_color);
    setRadius(savedValues.radius);
    setShadow(savedValues.shadow);
    setLauncherLabel(savedValues.launcher_label);
    setLauncherPosition(savedValues.launcher_position);
    setLauncherIcon(savedValues.launcher_icon);
    setLauncherCollapseMobile(savedValues.launcher_collapse_mobile);
    setHidePoweredBy(savedValues.hide_powered_by);
    setTheme(savedValues.theme);
  };

  // Re-hydrate local state when the client finishes loading.
  useMemo(() => {
    if (currentClient?.client.branding) reset();
  }, [currentClient]);

  const previewLogo = typeof logo === "string" ? logo : null;
  // Default the launcher label to the connector's agent name when left empty.
  const effectiveLabel =
    launcherLabel || t("branding.launcherDefault", { name: agentName });

  const branding: ClientBranding = {
    logo: previewLogo,
    theme_color: themeColor,
    radius,
    shadow,
    launcher_label: launcherLabel,
    launcher_position: launcherPosition,
    launcher_icon: launcherIcon,
    launcher_collapse_mobile: launcherCollapseMobile,
    hide_powered_by: hidePoweredBy,
    theme,
  };

  const dirty = logo instanceof File || differs(branding, savedValues);

  // Keep an out-of-range saved radius selectable rather than silently dropping it.
  const radiusPresets = RADIUS_OPTIONS.map((o) => ({
    ...o,
    label: t(`branding.radius.${o.label}`),
  }));
  const radiusOptions = radiusPresets.some((o) => o.value === radius)
    ? radiusPresets
    : [{ value: radius, label: `${radius}px` }, ...radiusPresets];

  const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const snippet = webConnector
    ? `<script
  src="${appOrigin}/embed.js"
  data-connector="${webConnector.id}"
  data-color="${themeColor}"
  data-label="${effectiveLabel}"
  data-position="${launcherPosition}"
  data-icon="${launcherIcon}"
  data-radius="${radius}"
  data-shadow="${shadow}"${
    // The frame around the chat is drawn by embed.js before the chat loads, so it
    // has to be told the theme the chat will wear. `auto` is its default.
    theme === "auto" ? "" : `\n  data-theme="${theme}"`
  }${
    // Only the *opt-out* is written: embed.js folds the launcher on a phone unless it
    // is told otherwise, so a snippet saying "true" would be noise on every site.
    launcherCollapseMobile ? "" : `\n  data-collapse-mobile="false"`
  }
  defer></script>`
    : null;

  const handleSave = async () => {
    if (!clientId) return;
    try {
      let logoValue: string | null | undefined = undefined;
      if (logo instanceof File) {
        logoValue = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(logo);
        });
      } else if (logo === null) {
        logoValue = null;
      } else {
        logoValue = logo;
      }

      await updateClient.mutateAsync({
        clientId,
        data: {
          branding: {
            logo: logoValue,
            theme_color: themeColor,
            radius,
            shadow,
            launcher_label: launcherLabel,
            launcher_position: launcherPosition,
            launcher_icon: launcherIcon,
            launcher_collapse_mobile: launcherCollapseMobile,
            hide_powered_by: hidePoweredBy,
            theme,
          },
        },
      });
      toast.success(t("branding.saved"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("branding.saveError"),
      );
    }
  };

  return (
    <PageLayout
      width="medium"
      title={t("branding.title")}
      description={t("branding.description")}
    >
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
        {/* Controls */}
        <div>
          {/* ── Chat ─────────────────────────────────────────── */}
          <SettingsSection
            stacked
            title={t("branding.chatSection")}
            description={t("branding.chatSectionDescription")}
          >
            <Field label={t("branding.logo")} hint={t("branding.logoHint")}>
              {previewLogo ? (
                <div className="flex items-center gap-4">
                  <img
                    src={previewLogo}
                    alt={t("branding.logoAlt")}
                    className="size-14 rounded-full border object-cover"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLogo(null)}
                    disabled={!isOwner}
                  >
                    {t("branding.removeLogo")}
                  </Button>
                </div>
              ) : (
                <div className="max-w-[240px]">
                  <FileUpload
                    type="image"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml"
                    maxSize={2 * 1024 * 1024}
                    value={logo instanceof File ? logo : null}
                    onChange={(f) =>
                      setLogo(Array.isArray(f) ? f[0] || null : f)
                    }
                    disabled={!isOwner}
                    className="p-4"
                  />
                </div>
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("branding.themeColor")} htmlFor="color">
                <div className="flex items-center gap-3">
                  <ColorSwatch
                    value={themeColor}
                    onChange={setThemeColor}
                    disabled={!isOwner}
                  />
                  <Input
                    id="color"
                    value={themeColor}
                    onChange={(e) => setThemeColor(e.target.value)}
                    disabled={!isOwner}
                    className="max-w-[140px] font-mono"
                  />
                </div>
              </Field>

              <Field label={t("branding.radiusLabel")} htmlFor="radius">
                <Select
                  value={String(radius)}
                  onValueChange={(v) => setRadius(Number(v))}
                  disabled={!isOwner}
                >
                  <SelectTrigger id="radius">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {radiusOptions.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label} — {o.value}px
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("branding.shadow")} htmlFor="shadow">
                <Select
                  value={shadow || "none"}
                  onValueChange={(v) =>
                    setShadow(v as NonNullable<ClientBranding["shadow"]>)
                  }
                  disabled={!isOwner}
                >
                  <SelectTrigger id="shadow">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t("branding.shadows.none")}
                    </SelectItem>
                    <SelectItem value="sm">
                      {t("branding.shadows.sm")}
                    </SelectItem>
                    <SelectItem value="md">
                      {t("branding.shadows.md")}
                    </SelectItem>
                    <SelectItem value="lg">
                      {t("branding.shadows.lg")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {/* The frame embed.js draws around the chat follows it too, which
                  is why the snippet below carries it as `data-theme`. */}
              <Field
                label={t("branding.theme")}
                htmlFor="chat-theme"
                hint={t("branding.themeHint")}
              >
                <Select
                  value={theme}
                  onValueChange={(v) =>
                    setTheme(v as NonNullable<ClientBranding["theme"]>)
                  }
                  disabled={!isOwner}
                >
                  <SelectTrigger id="chat-theme">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      {t("branding.themes.auto")}
                    </SelectItem>
                    <SelectItem value="light">
                      {t("branding.themes.light")}
                    </SelectItem>
                    <SelectItem value="dark">
                      {t("branding.themes.dark")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              orientation="horizontal"
              className="rounded-lg border p-3"
              label={t("branding.hidePoweredBy")}
              htmlFor="hide-powered-by"
              hint={t("branding.hidePoweredByHint")}
            >
              <Switch
                id="hide-powered-by"
                checked={hidePoweredBy}
                onCheckedChange={setHidePoweredBy}
                disabled={!isOwner}
              />
            </Field>
          </SettingsSection>

          {/* ── Bouton ───────────────────────────────────────── */}
          <SettingsSection
            stacked
            title={t("branding.launcherSection")}
            description={t("branding.launcherSectionDescription")}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label={t("branding.launcherLabel")}
                htmlFor="launcher-label"
                hint={t("branding.launcherLabelHint", {
                  label: t("branding.launcherDefault", { name: agentName }),
                })}
              >
                <Input
                  id="launcher-label"
                  value={launcherLabel}
                  onChange={(e) => setLauncherLabel(e.target.value)}
                  placeholder={t("branding.launcherDefault", {
                    name: agentName,
                  })}
                  disabled={!isOwner}
                />
              </Field>
              <Field
                label={t("branding.launcherPosition")}
                htmlFor="launcher-position"
              >
                <Select
                  value={launcherPosition}
                  onValueChange={(v) =>
                    setLauncherPosition(v as "right" | "left")
                  }
                  disabled={!isOwner}
                >
                  <SelectTrigger id="launcher-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="right">
                      {t("branding.positions.right")}
                    </SelectItem>
                    <SelectItem value="left">
                      {t("branding.positions.left")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("branding.launcherIcon")} htmlFor="launcher-icon">
                <Select
                  value={launcherIcon}
                  onValueChange={(v) => setLauncherIcon(v as "chat" | "phone")}
                  disabled={!isOwner}
                >
                  <SelectTrigger id="launcher-icon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chat">
                      {t("branding.icons.chat")}
                    </SelectItem>
                    <SelectItem value="phone">
                      {t("branding.icons.phone")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field
              orientation="horizontal"
              className="rounded-lg border p-3"
              label={t("branding.collapseMobile")}
              htmlFor="collapse-mobile"
              hint={t("branding.collapseMobileHint")}
            >
              <Switch
                id="collapse-mobile"
                checked={launcherCollapseMobile}
                onCheckedChange={setLauncherCollapseMobile}
                disabled={!isOwner}
              />
            </Field>
          </SettingsSection>

          {/* Install snippet */}
          <SettingsSection
            stacked
            title={t("branding.snippetTitle")}
            description={
              snippet ? (
                // `<code/>` is self-closing in the locale files: the tag is
                // markup, and Trans would parse it out of an interpolated value.
                <Trans
                  i18nKey="connectors.branding.snippetDescription"
                  components={{ code: <code>{"</body>"}</code> }}
                />
              ) : (
                t("branding.snippetMissing")
              )
            }
          >
            {snippet && (
              <div className="flex items-start gap-2">
                <pre className="flex-1 overflow-x-auto rounded-md border bg-muted/50 p-3 text-xs font-mono whitespace-pre">
                  {snippet}
                </pre>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(snippet);
                    toast.success(t("form.webSnippetCopied"));
                  }}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            )}
          </SettingsSection>

          {isOwner && (
            <FormFooter
              dirty={dirty}
              saving={updateClient.isPending}
              onSave={handleSave}
              onCancel={reset}
            />
          )}
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-8 lg:self-start">
          <p className="mb-3 text-sm font-medium text-muted-foreground">
            {t("branding.previewChat")}
          </p>
          <BrandingPreview
            branding={branding}
            agentName={agentName}
            clientName={
              currentClient?.client.name || t("branding.previewCompany")
            }
          />

          <p className="mb-3 mt-6 text-sm font-medium text-muted-foreground">
            {t("branding.previewLauncher")}
          </p>
          <FakeSite position={launcherPosition}>
            <LauncherPreview
              branding={branding}
              label={effectiveLabel}
              logo={previewLogo}
            />
          </FakeSite>
        </div>
      </div>
    </PageLayout>
  );
}

// A clean circular colour swatch — the native <input type="color"> sits on top,
// invisible, so we avoid the "square inside a rounded box" default rendering.
function ColorSwatch({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation("connectors");
  return (
    <div className="relative size-9 shrink-0">
      <div
        className="size-9 rounded-full border shadow-sm"
        style={{ backgroundColor: value }}
      />
      <input
        type="color"
        aria-label={t("branding.themeColor")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
      />
    </div>
  );
}

// A tiny fake browser window so the launcher is previewed in context, on a
// surface distinct from the settings form.
function FakeSite({
  position,
  children,
}: {
  position: "right" | "left";
  children: React.ReactNode;
}) {
  const { t } = useTranslation("connectors");
  return (
    <div className="overflow-hidden rounded-xl border bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 border-b bg-background/60 px-3 py-2">
        <span className="size-2.5 rounded-full bg-red-400" />
        <span className="size-2.5 rounded-full bg-amber-400" />
        <span className="size-2.5 rounded-full bg-emerald-400" />
        <div className="ml-2 flex-1 truncate rounded-md bg-muted px-3 py-1 text-xs text-muted-foreground">
          {t("branding.previewSite")}
        </div>
      </div>
      {/* Page body with placeholder content + floating launcher */}
      <div className="relative h-56 overflow-hidden p-5">
        <div className="space-y-3">
          <div className="h-6 w-2/3 rounded bg-muted" />
          <div className="h-3 w-full rounded bg-muted/70" />
          <div className="h-3 w-5/6 rounded bg-muted/70" />
          <div className="h-3 w-4/6 rounded bg-muted/70" />
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="h-16 rounded-lg bg-muted/60" />
            <div className="h-16 rounded-lg bg-muted/60" />
            <div className="h-16 rounded-lg bg-muted/60" />
          </div>
        </div>
        <div
          className={cn(
            "absolute bottom-4",
            position === "right" ? "right-4" : "left-4",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

// A faithful, non-interactive copy of the embed launcher button, at its real size
// (embed.js: 44px avatar, 8px padding, 15px semibold label) — it is the one thing
// in this preview a tenant sees exactly as their visitors will.
function LauncherPreview({
  branding,
  label,
  logo,
}: {
  branding: ClientBranding;
  label: string;
  logo?: string | null;
}) {
  const color = branding.theme_color || DEFAULT_COLOR;
  const Icon =
    branding.launcher_icon === "phone" ? PhoneIcon : ChatCircleDotsIcon;
  return (
    <div
      className={cn(
        // The launcher is white on every site, whatever the chat's theme: it sits on
        // the tenant's page, not in the panel.
        "inline-flex items-center gap-[11px] rounded-full border border-black/5 bg-white p-2 shadow-lg",
        label && "pr-5",
      )}
    >
      <span className="relative">
        <span
          className="flex size-11 items-center justify-center overflow-hidden rounded-full text-white"
          style={{ backgroundColor: color }}
        >
          {logo ? (
            <img src={logo} alt="" className="size-full object-cover" />
          ) : (
            <Icon className="size-[22px]" weight="fill" />
          )}
        </span>
        <span className="absolute -bottom-px -right-px size-3 rounded-full bg-success ring-2 ring-white" />
      </span>
      {label && (
        <span className="whitespace-nowrap text-[15px] font-semibold text-slate-900">
          {label}
        </span>
      )}
    </div>
  );
}

// A compact, faithful preview of the themed chat using the real chat components.
function BrandingPreview({
  branding,
  agentName,
  clientName,
}: {
  branding: ClientBranding;
  agentName: string;
  clientName: string;
}) {
  const { t } = useTranslation("connectors");
  const endRef = useRef<HTMLDivElement>(null);
  const now = Date.now();
  const sessionInfo: PublicSessionInfo = {
    agent: { id: "preview", name: agentName },
    client: {
      id: "preview",
      name: clientName,
      address: "",
      logo: branding.logo || "",
    },
    branding,
    phoneNumber: null,
    agents: { preview: { name: agentName } },
  };
  const messages: SessionMessage[] = [
    {
      type: "agent",
      value: t("branding.previewGreeting", { name: agentName }),
      timestamp: now - 120000,
    },
    {
      type: "user",
      value: t("branding.previewQuestion"),
      timestamp: now - 60000,
    },
    {
      type: "agent",
      value: t("branding.previewAnswer"),
      timestamp: now - 30000,
    },
  ];

  // The real frame, at the embed panel's size. It is drawn in the back-office's own
  // theme, not the one picked above: the colour tokens resolve on `<html>`, so a
  // subtree cannot be switched on its own, and flipping the whole settings page to
  // show a dark chat would be worse than previewing its light twin.
  return (
    <ChatLayout
      variant="preview"
      branding={branding}
      header={<ChatHeader sessionInfo={sessionInfo} />}
      messages={
        <ChatMessages
          allMessages={messages}
          streamingChunk=""
          messagesEndRef={endRef}
          showAgentAvatar
          agentName={agentName}
          agentLogo={branding.logo}
        />
      }
      input={
        <ChatInput
          textInput=""
          setTextInput={() => {}}
          onSubmit={(e) => e.preventDefault()}
          disabled={false}
          agentName={agentName}
          hidePoweredBy={branding.hide_powered_by ?? false}
        />
      }
    />
  );
}
